import React, { useState, useEffect } from 'react'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import PropTypes from 'prop-types'

import {
    Box,
    Text,
    Image,
    Link as ChakraLink,

    /* Hooks */
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Styles */
import { useMyRewardsStyles } from '../styles/myRewardsStyles'

/* Components */
import ResponsivePicture from '../../../components/responsive-picture'
import { renderComponentsSwitch } from '../../../components/optimizely/utils/contentRenderer'

/* Hooks */
import { AuthModal, useAuthModal } from '../../../hooks/use-auth-modal'
import { useHistory } from 'react-router-dom'
import { useOptimizely } from '../../../contexts/optimizely-context'

/* Utils */
import { generateStyles } from '../../../components/optimizely/utils/utils'

const DownloadApp = ({
    data,
    variant = 'auto'
}) => {
    const styles = useMyRewardsStyles()
    const tapStyle = styles?.components?.tilesAndApp

    const detectedVariant = useBreakpointValue(
        { base: 'mobile', lg: 'desktop' }
    )

    const activeVariant = variant === 'auto' ? detectedVariant : variant

    if (activeVariant === 'mobile') {
        return (
            <Box {...tapStyle.dAppWrapper}>
                <Box {...styles.overlay}></Box>
                <Image src={data?.mobile?.image?.src} alt={data?.mobile?.image?.alt} w={'100%'} />
                <Box {...tapStyle.dAppContent}>
                    <Text {...generateStyles(data?.BannerDownloadAppBlockTitleStyle, {...tapStyle.dAppTitle})}>
                        <Image src={data?.mobile?.icon} {...tapStyle.dAppIcon} />
                        {data?.title}
                    </Text>

                    <Box {...tapStyle.dAppLogos}>
                        {data?.logos?.map((logo, index) => {
                            return <ChakraLink
                                isExternal
                                target="_blank"
                                rel="noopener noreferrer"
                                href={logo?.url}>
                                <Image src={logo?.logo} />
                            </ChakraLink>
                        })}
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box {...tapStyle.dAppWrapper}>
            <Text {...generateStyles(data?.BannerDownloadAppBlockTitleStyle, {...tapStyle.dAppTitle})}>
                {data?.title}
            </Text>
            <Box {...tapStyle.dAppLogos}>
                {data?.logos?.map((logo) => {
                    return <Box {...tapStyle.dAppLogoQr}>
                        <Image w={'100%'} src={logo?.qrCode} />
                        <Image w={'100%'} src={logo?.logo} />
                    </Box>
                })}
            </Box>
        </Box>
    )
}

DownloadApp.propTypes = {
    data: PropTypes.object,
    variant: PropTypes.oneOf(['auto', 'mobile', 'desktop'])
}

const Tile = ({
    image,
    icon,
    url,
    title,
    setOnClick
}) => {
    const styles = useMyRewardsStyles()
    const tapStyle = styles?.components?.tilesAndApp

    return (
        <Box {...tapStyle.tileWrapper}>
            <Box {...styles.overlay}></Box>
            <Box>
                <ResponsivePicture {...image} imageProps={{ w: '100%' }} />
                <Box {...tapStyle.tileContent}>
                    <Text {...tapStyle.tileTitle} onClick={() => setOnClick(url)}>
                        <Image src={icon} {...tapStyle.tileIcon} />
                        {title}
                    </Text>
                </Box>
            </Box>
        </Box>
    )
}

Tile.propTypes = {
    image: PropTypes.object,
    icon: PropTypes.string,
    url: PropTypes.string,
    title: PropTypes.string,
    setOnClick: PropTypes.func
}

const TilesAndApp = () => {
    const { rewardPageBlock } = useOptimizely()
    const styles = useMyRewardsStyles()
    const tapStyle = styles?.components?.tilesAndApp
    const authModal = useAuthModal()
    const history = useHistory()
    const [cmsData, setCmsData] = useState(null)

    // This will fallback CMS
    const hardCodedData = {
        downloadApp: {
            title: 'Download the Garage App & Get Rewarded',
            logos: [{
                logo: getAssetUrl('static/img/my-rewards/apple-logo.svg'),
                qrCode: getAssetUrl('static/img/my-rewards/apple-qr.svg'),
                url: 'https://www.apple.com/ca/app-store/'
            }, {
                logo: getAssetUrl('static/img/my-rewards/android-logo.svg'),
                qrCode: getAssetUrl('static/img/my-rewards/android-qr.svg'),
                url: 'https://play.google.com/store/games?hl=en'
            }],
            mobile: {
                icon: getAssetUrl('static/img/my-rewards/right-arrow.svg'),
                image: {
                    src: getAssetUrl('static/img/my-rewards/app-image-mobile.jpg'),
                    alt: 'Download our app & get rewarded'
                }
            }
        },
        tiles: [{
            image: {
                mobileSrc: getAssetUrl('static/img/my-rewards/tile-1-mobile.jpg'),
                desktopSrc: getAssetUrl('static/img/my-rewards/tile-1-desktop.jpg'),
                alt: 'join our community'
            },
            icon: getAssetUrl('static/img/my-rewards/right-arrow.svg'),
            title: 'join our community',
            url: '/my-rewards'
        }, {
            image: {
                mobileSrc: getAssetUrl('static/img/my-rewards/tile-2-mobile.jpg'),
                desktopSrc: getAssetUrl('static/img/my-rewards/tile-2-desktop.jpg'),
                alt: 'sign in'
            },
            icon: getAssetUrl('static/img/my-rewards/right-arrow.svg'),
            title: 'sign in',
            url: 'signIn'
        }]
    }

    const cmsDataModel = (rewardPageBlockModel) => {
        const DownloadAppBannerMedia = rewardPageBlockModel?.DownloadAppBannerMedia?.[0]?.ContentLink?.Expanded;

        if (!DownloadAppBannerMedia || DownloadAppBannerMedia.__typename !== 'BannerDownloadAppBlock') {
            return null;
        }

        const logos = DownloadAppBannerMedia.QRCodeImageList?.map((qrItem) => ({
            logo: qrItem.Expanded?.Image?.Expanded?.Url,
            qrCode: qrItem.Expanded?.QRCodeMedia?.Expanded?.Url,
            url: qrItem.Expanded?.Link?.Expanded?.Uri
        })) || [];

        return {
            downloadApp: {
                backgroundColor: DownloadAppBannerMedia.BackgroundColor,
                title: DownloadAppBannerMedia.Title,
                BannerDownloadAppBlockTitleStyle: DownloadAppBannerMedia.BannerDownloadAppBlockTitleStyle,
                logos: [...logos],
                mobile: {
                    icon: getAssetUrl('static/img/my-rewards/right-arrow.svg'),
                    image: {
                        src: DownloadAppBannerMedia.BackgroundImage?.Expanded?.DesktopMedia?.Expanded?.Url,
                        alt: DownloadAppBannerMedia.BackgroundImage?.Expanded?.AltText
                    }
                }
            }
        }
    }

    const handleTileClick = (url) => {
        if (url === 'signIn') {
            authModal.onOpen()
        } else {
            history.push(url)
        }
    }

    useEffect(() => {
        if (rewardPageBlock) {
            setCmsData(cmsDataModel(rewardPageBlock))
        } else {
            setCmsData(hardCodedData)
        }
    }, [rewardPageBlock])

    return (
        <Box {...tapStyle.container}>
            {cmsData?.downloadApp &&
                <Box {...tapStyle.downloadAppContainer} backgroundColor={cmsData?.downloadApp?.backgroundColor}>
                    <Box {...styles.componentWrapper}>
                        <DownloadApp data={cmsData?.downloadApp} />
                    </Box>
                </Box>
            }
            <Box {...tapStyle.tilesContainer} {...styles.componentWrapper}>
                {rewardPageBlock ? (
                    rewardPageBlock?.BannerSection?.map((tile) => {
                        return renderComponentsSwitch(tile?.ContentLink?.Expanded, {
                            widgetTileBlockImageOverlay: true,
                            overwriteWidgetTileBlockTextCtaStyle: {
                                fontSize: {base: '24px', lg: '32px'},
                                lineHeight: {base: '100%'}
                            },
                            customMediaContainerStyling: {
                                width: {base: '22px', lg: '39px'},
                                height: {base: '22px', lg: '39px'},
                                marginRight: {base: '8px', lg: '15px'},
                                marginTop: {base: '4px', lg: '8px'}
                            }
                        })
                    })
                ) : (
                    cmsData?.tiles?.map((tile, tileIndex) => {
                        return <Tile key={`tile-${tileIndex}`} {...tile} setOnClick={handleTileClick} />
                    })
                )}
            </Box>
            <AuthModal {...authModal} />
        </Box>
    )
}

export default TilesAndApp
